import { Loading } from '@/core/ui/components/Loading';
import React, { useState, useEffect } from 'react';
import { useI18n } from '@/shared/i18n';
import { useServices, useToast, getErrorMessage } from '@/app/providers';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useFileUpload } from '@/shared/hooks';
import { ProfileHeader } from '../components/ProfileHeader';
import { ProfileTabs, type ProfileTab } from '../components/ProfileTabs';
import { ProfileInfoTab } from '../components/ProfileInfoTab';
import { FamilySection } from '../components/FamilySection';
import { VehicleSection } from '@/modules/access-control/ui/components/VehicleSection';
import { PetSection } from '@/modules/pets/ui/components/PetSection';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { StaffProfile } from '@/presentation/components/organisms/StaffProfile';
import type { FiscalData, HouseholdMember } from '@/shared/types';

export const ProfilePage = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { userRepo, updateCurrentUser, setCurrentUser } = useServices();
  const { user, setUser, loading } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<ProfileTab>('INFO');
  const [familyLoaded, setFamilyLoaded] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Load household members when switching to FAMILY tab
  useEffect(() => {
    if (activeTab === 'FAMILY' && user && !familyLoaded) {
      const loadMembers = async () => {
        try {
          const members = await userRepo.getHouseholdMembers();
          setUser(prev => prev ? { ...prev, householdMembers: members } : null);
          setFamilyLoaded(true);
        } catch (error) {
          console.error('Error loading household members:', error);
          setFamilyLoaded(true);
        }
      };
      loadMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, familyLoaded]);

  // File upload hook
  const { upload: uploadAvatar, isUploading: avatarLoading } = useFileUpload('avatars', {
    allowedTypes: ['image/*'],
  });

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && user) {
      try {
        const result = await uploadAvatar(file);
        if (result) {
          // Use updateProfile with avatarFileId for the new endpoint
          await userRepo.updateProfile({
            avatarUrl: result.url,
            avatarFileId: result.id,
          });
          setUser({ ...user, avatarUrl: result.url, avatarFileId: result.id });
          updateCurrentUser({ avatarUrl: result.url, avatarFileId: result.id });
          toast.success('Avatar actualizado correctamente');
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleUpdateFiscal = async (fiscalData: FiscalData) => {
    if (!user) return;
    // Use the dedicated fiscal data endpoint
    const updatedFiscal = await userRepo.upsertFiscalData(fiscalData);
    setUser({ ...user, fiscalData: updatedFiscal });
  };

  const handleUpdateContact = async (data: { name: string; phone: string }) => {
    if (!user) return;
    const updated = await userRepo.updateProfile(data);
    const merged = { ...user, ...updated };
    setUser(merged);
    // Refleja el cambio en la sidebar/header al toque, sin un segundo PUT
    // (updateCurrentUser del AuthContext ya haría su propia llamada al repo).
    setCurrentUser(merged);
  };

  const handleAddMember = async (memberData: Omit<HouseholdMember, 'id' | 'accountStatus'>) => {
    if (!user) return;
    try {
      const newMember = await userRepo.createHouseholdMember(memberData);
      setUser(prev => prev ? {
        ...prev,
        householdMembers: [...(prev.householdMembers || []), newMember]
      } : null);
      toast.success('Miembro agregado correctamente');
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al agregar miembro');
      throw error;
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!user) return;
    try {
      await userRepo.deleteHouseholdMember(memberId);
      setUser(prev => prev ? {
        ...prev,
        householdMembers: prev.householdMembers?.filter(m => m.id !== memberId)
      } : null);
      toast.success('Miembro eliminado correctamente');
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al eliminar miembro');
      throw error;
    }
  };

  const handleResendInvitation = async (memberId: string) => {
    try {
      await userRepo.resendHouseholdMemberInvitation(memberId);
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al reenviar invitación');
      throw error;
    }
  };

  if (loading || !user) {
    return <Loading />;
  }

  // Determine effective role (tenantRole takes priority over role)
  const effectiveRole = (user as any).tenantRole || user.role;

  // Guards and Admins see the simplified StaffProfile
  if (effectiveRole === 'GUARD' || effectiveRole === 'ADMIN') {
    return <StaffProfile />;
  }

  // Residents see the full profile with tabs
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <ProfileHeader user={user} onAvatarUpload={handleAvatarUpload} />
      <ProfileTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="bg-surface rounded-b-xl shadow-sm border border-t-0 border-theme p-6 min-h-[300px]">
        {activeTab === 'INFO' && (
          <ProfileInfoTab
            user={user}
            onUpdateFiscal={handleUpdateFiscal}
            onUpdateContact={handleUpdateContact}
            onChangePassword={() => setShowPasswordModal(true)}
          />
        )}

        {activeTab === 'FAMILY' && (
          <FamilySection
            user={user}
            onAddMember={handleAddMember}
            onDeleteMember={handleDeleteMember}
            onResendInvitation={handleResendInvitation}
          />
        )}

        {activeTab === 'VEHICLES' && (
          <VehicleSection userId={user.id} userName={user.name} />
        )}

        {activeTab === 'PETS' && (
          <PetSection userId={user.id} />
        )}
      </div>

      {/* Modal de cambiar contraseña */}
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
};
